import Pusher, { type Channel } from "pusher-js";
import { getAccessToken } from "@/features/auth/services/accessToken.service";
import { getApiUrl } from "@/shared/utils/api";
import { CHAT_ENDPOINTS } from "../configs/chat.config";

let pusherInstance: Pusher | null = null;
const ROOM_CHANNEL_PREFIX = "presence-chat-";
const channels = new Map<string, Channel>();
const refCounts = new Map<string, number>();

function getPusher(): Pusher {
  if (!pusherInstance) {
    const authEndpoint = getApiUrl(CHAT_ENDPOINTS.PUSHER_AUTH);

    pusherInstance = new Pusher(import.meta.env.VITE_PUSHER_KEY, {
      cluster: import.meta.env.VITE_PUSHER_CLUSTER,
      authorizer: (channel) => ({
        authorize: (socketId, callback) => {
          const token = getAccessToken();
          if (!token) {
            callback(new Error("No access token"), null);
            return;
          }

          const params = new URLSearchParams();
          params.append("socket_id", socketId);
          params.append("channel_name", channel.name);

          fetch(authEndpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              "Authorization": `Bearer ${token}`,
            },
            body: params,
            credentials: "include",
          })
            .then((response) => {
              if (!response.ok) throw new Error(`Auth failed: ${response.status}`);
              return response.json();
            })
            .then((data) => callback(null, data))
            .catch((error) => callback(error, null));
        },
      }),
    });
  }
  return pusherInstance;
}

export function subscribeChannel(channelName: string): Channel {
  const pusher = getPusher();
  let channel = channels.get(channelName);
  if (!channel) {
    channel = pusher.subscribe(channelName);
    channels.set(channelName, channel);
  }
  refCounts.set(channelName, (refCounts.get(channelName) ?? 0) + 1);
  return channel;
}

export function unsubscribeChannel(channelName: string): void {
  const count = (refCounts.get(channelName) ?? 0) - 1;
  if (count > 0) {
    refCounts.set(channelName, count);
    return;
  }
  refCounts.delete(channelName);
  const pusher = getPusher();
  const channel = channels.get(channelName);
  if (channel) {
    pusher.unsubscribe(channelName);
    channels.delete(channelName);
  }
}

/**
 * Re-runs the auth handshake of a joined channel. Unlike unsubscribeChannel +
 * subscribeChannel, the Channel object is kept, so the hooks' event bindings
 * and the ref count survive.
 */
export function resubscribeChannel(channelName: string): void {
  const channel = channels.get(channelName);
  if (!channel) return;
  channel.unsubscribe();
  channel.subscribe();
}

/**
 * The server signs the élu's `hidden` flag into the presence `user_info` only
 * when a channel is joined, so every open room must redo the handshake to pick
 * up a visibility change.
 */
export function resubscribeRoomChannels(): void {
  channels.forEach((_channel, channelName) => {
    if (channelName.startsWith(ROOM_CHANNEL_PREFIX)) resubscribeChannel(channelName);
  });
}

export function getChannel(channelName: string): Channel | undefined {
  return channels.get(channelName);
}

export function disconnectChatPusher(): void {
  if (pusherInstance) {
    pusherInstance.disconnect();
    channels.clear();
    refCounts.clear();
    pusherInstance = null;
  }
}
